import { RouteRecordRaw, RouteLocationNamedRaw } from 'vue-router';

/**
 * Single source of truth for learner route names.
 *
 * Navigate by name, never by path — see .claude/rules/code-standards.md. Changing a route's `path` should
 * be a one-line edit in routes.ts; any call site that hardcodes a path silently 404s instead.
 *
 * Keys are camelCase for call sites; values are the literal `name` registered on the route
 * record. Values MUST stay byte-identical to routes.ts — route-names.spec.ts asserts the two
 * sets map 1:1, which is the enforcement that actually runs in CI.
 *
 * Admin has its own copy at @admin/router/route-names — the two apps share several names
 * (home, courses, logout) but not the routes behind them, so these must not be merged.
 */
export const LEARNER_ROUTES = {
  home: 'home',
  courses: 'courses',
  profile: 'profile',
  login: 'login',
  loginDefault: 'login-default',
  logout: 'logout',
  resetPassword: 'reset-password',
  coursePlayer: 'course-player', // NB: param is OPTIONAL, :id(\d+)? — resolves bare too
  courseError: 'course-error',
  loginError: 'login-error',
  trialRegistration: 'trial-registration',
  campaignsUnsubscribe: 'campaigns-unsubscribe',
  accessDenied: 'access-denied',
  publicLogout: 'public-logout',
  notFound: 'not-found',
} as const;

export type LearnerRouteName = typeof LEARNER_ROUTES[keyof typeof LEARNER_ROUTES];

/**
 * `Omit` does not distribute over unions, and RouteRecordRaw is a 5-member union
 * (SingleView | SingleViewWithChildren | MultipleViews | MultipleViewsWithChildren |
 * Redirect). A naked Omit<RouteRecordRaw, 'name'> collapses to the intersection of their
 * keys and silently drops `component` / `components`. The `T extends unknown ?` wrapper
 * forces distribution so each union member keeps its own shape.
 *
 * TypeScript is pinned at 4.8, which has no `satisfies` operator, so annotating the route
 * array with this type is how a typo in a route DEFINITION gets caught.
 */
type WithName<T, N extends string> = T extends unknown ? Omit<T, 'name'> & { name: N } : never;

export type LearnerRouteRecord = WithName<RouteRecordRaw, LearnerRouteName>;

/**
 * A route location that cannot be a path. Use as the return type of any helper that builds
 * a navigation target, and as the type of a `to` prop.
 */
export interface LearnerRouteTo extends Omit<RouteLocationNamedRaw, 'name'> {
  name: LearnerRouteName;
}
